import React, { useState, useEffect, useMemo } from 'react';
import apiClient from '../utils/apiClient';
import { useTable, usePagination, useGlobalFilter, useSortBy } from 'react-table';
import Swal from 'sweetalert2';
import { SERVER_BASE } from '../config/server';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV, faEdit, faTrash, faPlus, faPencil } from "@fortawesome/free-solid-svg-icons";
import moment from 'moment';


const ItemGroupPage = ({ permissions }) => {
  const [itemGroups, setItemGroups] = useState([]);
  const [itemGroupName, setItemGroupName] = useState('');
  const [itemGroupCode, setItemGroupCode] = useState('');
  const [inclusions, setInclusions] = useState('');
  const [exclusions, setExclusion] = useState('');
  const [editIndex, setEditIndex] = useState(null);
  const email = localStorage.getItem('email');
  const code = localStorage.getItem('code');
  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role');
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    fetchItemGroups();
  }, []);

  const fetchItemGroups = async () => {

    try {
      setLoadingData(true); // start loading
      const response = await apiClient.get(SERVER_BASE + '/item-groups', {
        params: { code },
        headers: {
          Authorization: `Bearer ${token}`, // Add the JWT token in the header
        },

      });
      setItemGroups(response.data);
    } catch (error) {
      console.error('Error fetching item groups:', error);
    }
    finally {
      setLoadingData(false);
    }
  };

  const handleItemGroupChange = (e) => setItemGroupName(e.target.value);
  const handleItemGroupCodeChange = (e) => setItemGroupCode(e.target.value);
  const handleItemGroupInclusionsChange = (e) => setInclusions(e.target.value);
  const handleItemGroupExclusionsChange = (e) => setExclusion(e.target.value);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!itemGroupName.trim() || !itemGroupCode.trim()) {
      Swal.fire('Please complete all fields', '', 'warning');
      return;
    }

    Swal.fire({
      title: editIndex !== null ? 'Confirm Update' : 'Confirm Create',
      text: editIndex !== null ? 'Are you sure you want to update this item group?' : 'Are you sure you want to create this item group?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'Cancel',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          let responseMessage = '';

          if (editIndex !== null) {
            const response = await apiClient.put(`${SERVER_BASE}/updateItemGroup/${itemGroups[editIndex].id}`,
              {
                name: itemGroupName,
                groupCode: itemGroupCode,
                inclusions,
                exclusions,
                code,
                email
              },

              {
                headers: {
                  Authorization: `Bearer ${token}`, // Add the JWT token in the header
                },
              });
            responseMessage = response.data || 'Inventory Group updated successfully';
            setEditIndex(null)
          } else {
            const response = await apiClient.post(SERVER_BASE + '/addItemGroup', {
              name: itemGroupName,
              groupCode: itemGroupCode,
              inclusions,
              exclusions,
              code,
              email
            }
              ,
              {
                headers: {
                  Authorization: `Bearer ${token}`, // Add the JWT token in the header
                },
              });
            responseMessage = response.data || 'Inventory Group created successfully';
          }

          setItemGroupName('');
          setItemGroupCode('');
          setExclusion('')
          setInclusions('')
          setEditIndex(null);
          fetchItemGroups();

          Swal.fire({
            icon: 'success',
            title: editIndex !== null ? 'Updated!' : 'Created!',
            text: responseMessage,
            position: 'top-end',
            toast: true,
            timer: 2000,
            showConfirmButton: false,
          });
        } catch (error) {
          console.error('Error saving item group:', error);
          Swal.fire({
            icon: 'error',
            text: error.response?.data || 'There was an error processing your request.',
            position: 'top-end',
            toast: true,
            timer: 2000,
            showConfirmButton: false,
          });
        }
      }
    });
  };

  const handleEdit = (index) => {
    setItemGroupName(itemGroups[index].groupName);
    setItemGroupCode(itemGroups[index].groupCode);
    setInclusions(itemGroups[index].inclusions);
    setExclusion(itemGroups[index].exclusions);
    setEditIndex(index);
  };

  const handleDelete = (index) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You will not be able to recover this item group!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await apiClient.delete(`${SERVER_BASE}/deleteItemGroup/${itemGroups[index].id}`, {
            data: { code },
            headers: {
              Authorization: `Bearer ${token}`, // Add the JWT token in the header
            },

          });
          fetchItemGroups();
          Swal.fire('Deleted!', 'Your item group has been deleted.', 'success');
        } catch (error) {
          console.error('Error deleting item group:', error);
        }
      }
    });
  };

  const columns = useMemo(
    () => [
      {
        Header: 'S.No',
        Cell: ({ row }) => row.index + 1,
      },
      {
        Header: 'IGC',
        accessor: 'groupCode',
      },
      {
        Header: 'Inventory Group Name',
        accessor: 'groupName',
      },
      {
        Header: 'Inclusions',
        accessor: 'inclusions',
        // Cell: ({ row }) => (
        //   row.original.inclusions ? (
        //     <>
        //       <FontAwesomeIcon
        //         icon={faInfoCircle}
        //         data-tooltip-id={`tooltip-inclusions-${row.index}`}
        //       // className="text-primary"
        //       />
        //       <Tooltip id={`tooltip-inclusions-${row.index}`} place="top">
        //         {row.original.inclusions}
        //       </Tooltip>
        //     </>
        //   ) : null
        // ),
      },
      {
        Header: 'Exclusions',
        accessor: 'exclusions',
        // Cell: ({ row }) => (
        //   row.original.exclusions ? (
        //     <>
        //       <FontAwesomeIcon
        //         icon={faInfoCircle}
        //         data-tooltip-id={`tooltip-exclusions-${row.index}`}
        //       // className="text-primary"
        //       />
        //       <Tooltip id={`tooltip-exclusions-${row.index}`} place="top">
        //         {row.original.exclusions}
        //       </Tooltip>
        //     </>
        //   ) : null
        // ),
      },
      {
        Header: "#",
        accessor: "Action",
        Cell: ({ row }) => {
          const {
            createdAt,
            createdByName,
            updatedAt,
            updatedByName,
          } = row.original;
          return (
            <div className="dropdown">
              <button
                className="btn btn-sm btn-light"
                type="button"
                id={`dropdownMenuButton-${row.index}`}
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <FontAwesomeIcon icon={faEllipsisV} />
              </button>

              <ul
                className="dropdown-menu"
                aria-labelledby={`dropdownMenuButton-${row.index}`}
              >
                {(role === "itManager" || permissions?.edit) && (
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handleEdit(row.index)}
                    >
                      <FontAwesomeIcon icon={faEdit} className="me-2" />
                      Edit
                    </button>
                  </li>
                )}

                {(role === "itManager" || permissions?.delete) && (
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handleDelete(row.index)}
                    >
                      <FontAwesomeIcon icon={faTrash} className="me-2" />
                      Delete
                    </button>
                  </li>
                )}
                {(createdByName || updatedByName) && (
                  <li>
                    <hr className="dropdown-divider" />
                  </li>
                )}
                {createdByName && (
                  <li className="dropdown-item">
                    <FontAwesomeIcon icon={faPlus} /> {createdByName}
                    {createdAt &&
                      ` (${moment(createdAt).format("DD-MMM-YY hh:mm A")})`}
                  </li>
                )}

                {/* Updated Info */}
                {updatedByName && (
                  <li className="dropdown-item">
                    <FontAwesomeIcon icon={faPencil} /> {updatedByName}
                    {updatedAt &&
                      ` (${moment(updatedAt).format("DD-MMM-YY hh:mm A")})`}
                  </li>
                )}
              </ul>
            </div>
          );
        },
      }
    ],
    [itemGroups]
  );


  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    setGlobalFilter,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, globalFilter },
  } = useTable(
    {
      columns,
      data: itemGroups,
      initialState: { pageIndex: 0, pageSize: 30 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );
  if (loadingData) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '60vh' }}>
        <div className="spinner-border text-success" role="status" style={{ width: '3rem', height: '3rem' }}>
          <span className="visually-hidden">Loading...</span>
        </div>
        <div className="mt-3 fs-5">Loading Item Groups...</div>
      </div>
    );
  }
  return (
    <div className="container mt-5">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>Inventory Group</h3>
        <div className="ledger-count">
          Total Groups: <strong>{itemGroups.length}</strong>
        </div>
      </div>
      {(role == 'itManager' || permissions?.write) && (<>
        <form onSubmit={handleSubmit} className="formcontainer mb-3">
          <div className="row align-items-end">
            <div className="form-group col-md-3">
              <label htmlFor="itemGroupName">Group Name</label>
              <input
                type="text"
                id="itemGroupName"
                className="form-control"
                value={itemGroupName}
                onChange={handleItemGroupChange}
                placeholder="Enter Group Name"
              />
            </div>
            <div className="form-group col-md-2">
              <label htmlFor="itemGroupCode">Group Code</label>
              <input
                type="text"
                id="itemGroupCode"
                className="form-control"
                value={itemGroupCode}
                onChange={handleItemGroupCodeChange}
                placeholder="Enter Group Code"
              />
            </div>
            <div className="form-group col-md-3">
              <label htmlFor="inclusions">Inclusions</label>
              <input
                type="text"
                id="inclusions"
                className="form-control"
                value={inclusions}
                onChange={handleItemGroupInclusionsChange}
                placeholder="Enter inclusions"
              />
            </div>
            <div className="form-group col-md-3">
              <label htmlFor="exclusions">Exclusions</label>
              <input
                type="text"
                id="exclusions"
                className="form-control"
                value={exclusions}
                onChange={handleItemGroupExclusionsChange}
                placeholder="Enter exclusions"
              />
            </div>
            <div className="col-md-2">
              <button type="submit" className="btn btn-outline-success mt-2">
                {editIndex !== null ? 'Update Group' : 'Add Group'}
              </button>
            </div>
          </div>
        </form>
      </>
      )}
      {(role == 'itManager' || permissions?.read) && (
        <div className="formcontainer table-responsive">
          <div className="d-flex justify-content-between gap-3">
            <input
              type="text"
              className="form-control"
              placeholder="Search item groups"
              value={globalFilter || ''}
              onChange={(e) => setGlobalFilter(e.target.value)}
            />

            <label className="d-inline-flex align-items-center ">
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="form-select w-auto"
              >
                {[30, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    Show {size}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <table {...getTableProps()} className="table table-striped">
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                      {column.render('Header')}
                      <span>
                        {column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                      </span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="d-flex align-items-center mb-3">
            {/* Previous – left corner */}
            <button
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
              className="btn btn-secondary me-auto"
              style={{ minWidth: 110 }}   // keeps width consistent
            >
              Previous
            </button>

            {/* Center block for page index & size selector */}
            <div className="text-center flex-grow-1 d-flex flex-column align-items-center">
              {/* Row 1: Page index and size selector in one line */}
              <div className="d-flex align-items-center gap-3">
                <div className="fw-semibold">
                  Page {pageIndex + 1} of {pageOptions.length}
                </div>


              </div>
            </div>


            {/* Next – right corner */}
            <button
              onClick={() => nextPage()}
              disabled={!canNextPage}
              className="btn btn-secondary ms-auto"
              style={{ minWidth: 110 }}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemGroupPage;
